import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Input,
  useToast,
} from '@chakra-ui/react';
import { CollabDataContext } from 'contexts/collabDataContext';
import { useLocation } from 'react-router-dom';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { deliverableApi } from 'services/api/deliverable';
import { getInfluencerPrimaryHandle } from 'utils/influencerUtils';
import ApprovedContentPreview from './ApprovedContentPreview';
import BottomSheet from './BottomSheet';
import BrandFeedbackBody from './BrandFeedbackBody';
import { SheetButton } from './counter-flow/sheetParts';
import { SubmissionSheetTitle, FeedbackBox, linkInputProps } from './submissionSheetParts';
import { ff } from '../../../theme';

interface InputLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliverableKey: string;
  postLink: string;
  linkIndex: number;
  showFeedback: boolean;
  rejectionReason: string | null;
  modalHeader: string;
  clientName: string;
  clientId: string;
  /** Approved draft to post, when the campaign ran a draft-approval round. */
  approvedContentUrl?: string;
}

const platformSubtitles: Record<string, string> = {
  tiktoks: 'Post to TikTok',
  instagramReels: 'Post to Instagram Reels',
  instagramPosts: 'Post to Instagram',
};

const platformPlaceholders: Record<string, string> = {
  tiktoks: 'Paste TikTok link...',
  instagramReels: 'Paste Instagram Reel link...',
  instagramPosts: 'Paste Instagram Post link...',
};

const InputLinkModal: React.FC<InputLinkModalProps> = ({
  isOpen,
  onClose,
  deliverableKey,
  postLink,
  linkIndex,
  showFeedback,
  rejectionReason,
  modalHeader,
  clientName,
  clientId,
  approvedContentUrl,
}) => {
  const toast = useToast();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const collabId = queryParams.get('id');
  const collabData = useContext(CollabDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const { influencerData } = useContext(InfluencerDataContext);
  const [newPostLink, setNewPostLink] = useState(postLink);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync the field with the incoming link whenever the modal (re)opens, since
  // this component stays mounted across deliverables and useState only seeds once.
  useEffect(() => {
    if (isOpen) setNewPostLink(postLink ?? '');
  }, [isOpen, postLink]);

  const handleClose = () => {
    setNewPostLink('');
    setIsSubmitting(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (newPostLink.trim() === '') {
      toast({ title: 'Link is required.', description: 'Please enter a valid link before confirming.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (!collab) {
      toast({ title: 'Error', description: 'Collaboration data not found.', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    setIsSubmitting(true);
    try {
      await deliverableApi.submitPost({
        collabId: collab.id,
        deliverableKey,
        linkIndex,
        postUrl: newPostLink,
        influencerHandle: getInfluencerPrimaryHandle(influencerData),
        clientName,
        clientId,
      }, influencerData?.id);
      toast({ title: 'Link submitted.', description: 'Your link has been submitted successfully.', status: 'success', duration: 3000, isClosable: true });
      handleClose();
    } catch (error) {
      toast({ title: 'Submission failed.', description: 'There was an error submitting your link.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const subtitle = platformSubtitles[deliverableKey] ?? 'Post your content';
  const placeholder = platformPlaceholders[deliverableKey] ?? 'Paste link here...';
  const canSubmit = newPostLink.trim() !== '' && !isSubmitting;

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      {/* Scrollable Content */}
      <Box overflowY="auto" px="20px" pt={1} pb="28px" fontFamily="display" color={ff.ink}>
        <SubmissionSheetTitle title={modalHeader} context={subtitle} />

        {/* Feedback */}
        {showFeedback && rejectionReason && (
          <Box mb="16px">
            <FeedbackBox lead="Edit requested:">
              <BrandFeedbackBody feedback={rejectionReason} />
            </FeedbackBox>
          </Box>
        )}

        <VStack spacing="16px" align="stretch">
          {/* APPROVED DRAFT */}
          {approvedContentUrl && (
            <ApprovedContentPreview
              url={approvedContentUrl}
              label="Approved draft"
              fileNameBase={modalHeader}
            />
          )}

          {/* POST LINK */}
          <Box>
            <Text as="label" htmlFor="ff-post-link" display="block" fontSize="14px" fontWeight={600} mb="8px">
              Post link
            </Text>
            <Input
              id="ff-post-link"
              value={newPostLink}
              onChange={(e) => setNewPostLink(e.target.value)}
              placeholder={placeholder}
              {...linkInputProps(false)}
            />
          </Box>

          {/* Submit Button */}
          <SheetButton
            tone="lime"
            onClick={handleConfirm}
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
            w="full"
            fontSize="16px"
          >
            Submit link
          </SheetButton>
        </VStack>
      </Box>
    </BottomSheet>
  );
};

export default InputLinkModal;
